import type { ChannelMode, ChannelRow } from "@/lib/api";

// Shared mode vocabulary for the Channels tab and the directory picker.
// Copy rules (UX spec): the noun is "Boo"; never "mode"/"silent"/"rejection"
// in user-visible text; "@-mentions" is allowed as a verb in hints only. The
// decline text's term of art is "not-available reply" — it must match the
// label of the reply bar and dialog in ChannelsTab.

export const MODES: readonly ChannelMode[] = [
  "always",
  "mentions",
  "posts_only",
  "never",
];

export const MODE_LABEL: Record<ChannelMode, string> = {
  always: "Answers everything",
  mentions: "Answers when called on",
  posts_only: "Posts only",
  never: "Stays quiet",
};

export const MODE_HINT: Record<ChannelMode, string> = {
  always: "Boo replies to every message posted here.",
  mentions:
    "Boo replies when someone @-mentions it, and keeps up with the follow-ups. Direct messages always get an answer.",
  posts_only:
    "Boo can post here when you ask it to — announcements and updates — but never replies to messages, not even @-mentions. No not-available reply either; it just stays quiet on anything coming in.",
  never:
    "Boo reads along and remembers, but doesn't join in. If someone @-mentions it, it declines with its not-available reply.",
};

// State dot rendered before mode labels — reinforcement only, text is
// always present.
export const MODE_DOT: Record<ChannelMode, string> = {
  always: "bg-emerald-500",
  mentions: "bg-sky-500",
  posts_only: "bg-amber-500",
  never: "bg-muted-foreground/40",
};

export const PLATFORM_LABEL: Record<ChannelRow["platform"], string> = {
  slack: "Slack",
  gchat: "Google Chat",
  teams: "Teams",
  discord: "Discord",
  dashboard_chat: "Dashboard",
};

// Row surface strings are loose: v2 brains emit dm/group_dm/channel/
// private_channel, rows migrated from v1 keep their old vocabulary
// (public_channel, space). Unknown strings render as-is.
export const ROW_SURFACE_LABEL: Record<string, string> = {
  dm: "Direct message",
  group_dm: "Group chat",
  channel: "Channel",
  public_channel: "Channel",
  private_channel: "Private channel",
  space: "Space",
};

// Rows from a pre-mode Boo carry only the derived boolean.
export const rowMode = (ch: ChannelRow): ChannelMode =>
  ch.mode ?? (ch.responding ? "mentions" : "never");

// Thread-flag approver keys use the channel-user vocabulary:
// `${platform}:${providerUserId}`.
export const channelUserKey = (u: { platform: string; providerUserId: string }) =>
  `${u.platform}:${u.providerUserId}`;
